'use client'

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { getAnonymousId } from './get-anonymous-id'
import type { VeritonicContextType, VeritonicProviderProps, VeritonicPurchasePayload } from './types'
import { VeritonicScript } from './VeritonicScript'

/**
 * Veritonic context
 */
const VeritonicContext = createContext<VeritonicContextType | null>(null)

/**
 * Veritonic Provider component that injects the script and provides tracking methods via context
 *
 * Automatically injects Veritonic script and initializes tracking.
 * Provides methods to track page views, funnel start, and funnel end.
 *
 * @example
 * ```tsx
 * // In your app root
 * <VeritonicProvider veritonicId="8nb4pl7r">
 *   <App />
 * </VeritonicProvider>
 *
 * // In any child component
 * function MyComponent() {
 *   const { trackView, isReady } = useVeritonic()
 *
 *   useEffect(() => {
 *     if (isReady) {
 *       trackView()
 *     }
 *   }, [isReady, trackView])
 *
 *   return <div>Content</div>
 * }
 * ```
 */
export const VeritonicProvider: React.FC<VeritonicProviderProps> = ({ children, veritonicId, gdpr }) => {
  const [isReady, setIsReady] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null)

  /**
   * Check if Veritonic script is loaded and ready
   */
  const checkVeritonicReady = (): boolean => {
    return typeof window !== 'undefined' && typeof window.vpr === 'function'
  }

  /**
   * Initialize Veritonic and wait for it to be ready
   */
  useEffect(() => {
    if (!veritonicId) {
      setError(new Error('Veritonic ID is required'))
      setIsLoading(false)
      return
    }

    // Check if already ready
    if (checkVeritonicReady()) {
      setIsReady(true)
      setIsLoading(false)
      return
    }

    // Poll for Veritonic to be ready (script loads asynchronously)
    checkIntervalRef.current = setInterval(() => {
      if (checkVeritonicReady()) {
        setIsReady(true)
        setIsLoading(false)
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current)
          checkIntervalRef.current = null
        }
      }
    }, 100)

    // Timeout after 10 seconds
    const timeout = setTimeout(() => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
        checkIntervalRef.current = null
      }
      if (!isReady) {
        setError(new Error('Veritonic script failed to load'))
        setIsLoading(false)
      }
    }, 10000)

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
      }
      clearTimeout(timeout)
    }
  }, [veritonicId, isReady])

  /**
   * Centralized call into the Veritonic global (`window.vpr`).
   *
   * Guards on readiness, logs explicitly when `window.vpr` is unavailable, and
   * swallows runtime errors so a tracking failure never breaks the host page.
   *
   * Attaches Segment `anonymous_id` when available. Intent commands may stay
   * bare; `purchase` always needs at least `{ type }`.
   */
  const callVeritonic = useCallback(
    (command: string, payload?: VeritonicPurchasePayload): void => {
      if (!isReady) {
        console.warn(`[Veritonic] Not ready. "${command}" call will be ignored.`)
        return
      }

      if (typeof window === 'undefined' || typeof window.vpr !== 'function') {
        console.warn(`[Veritonic] window.vpr is undefined. "${command}" call will be ignored.`)
        return
      }

      try {
        const anonymousId = getAnonymousId()
        const resolvedPayload = anonymousId ? { ...payload, anonymous_id: anonymousId } : payload

        if (resolvedPayload === undefined) {
          window.vpr(command)
        } else {
          window.vpr(command, resolvedPayload)
        }
      } catch (err) {
        console.error(`[Veritonic] Failed to track "${command}":`, err)
      }
    },
    [isReady]
  )

  const trackView = useCallback((): void => callVeritonic('view'), [callVeritonic])

  const trackFlowStart = useCallback((): void => callVeritonic('begin_enquiry'), [callVeritonic])

  const trackFlowComplete = useCallback((): void => callVeritonic('lead'), [callVeritonic])

  const trackScheduleMeeting = useCallback(
    (): void => callVeritonic('purchase', { type: 'Meeting Scheduled' }),
    [callVeritonic]
  )

  const contextValue = useMemo<VeritonicContextType>(
    () => ({
      isReady,
      isLoading,
      error,
      trackView,
      trackFlowStart,
      trackFlowComplete,
      trackScheduleMeeting,
    }),
    [isReady, isLoading, error, trackView, trackFlowStart, trackFlowComplete, trackScheduleMeeting]
  )

  return (
    <VeritonicContext.Provider value={contextValue}>
      <VeritonicScript veritonicId={veritonicId} gdpr={gdpr} />
      {children}
    </VeritonicContext.Provider>
  )
}

/**
 * Hook to access Veritonic context
 *
 * Must be used within a VeritonicProvider
 *
 * @throws {Error} If used outside of VeritonicProvider
 *
 * @returns Veritonic context with methods and state
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { trackView, isReady, isLoading } = useVeritonic()
 *
 *   if (isLoading) return <div>Loading Veritonic...</div>
 *
 *   return (
 *     <button onClick={() => trackView()}>
 *       Track View
 *     </button>
 *   )
 * }
 * ```
 */
export const useVeritonic = (): VeritonicContextType => {
  const context = useContext(VeritonicContext)

  if (!context) {
    throw new Error('useVeritonic must be used within a VeritonicProvider')
  }

  return context
}
